import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Flex,
  FormControl,
  HStack,
  IconButton,
  Input,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemProps,
  MenuList,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Portal,
  Text,
  ThemingProps,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  faCheck,
  faEllipsisH,
  faPen,
  faTimes,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { useRef, useState } from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DeleteMenuItem } from './DeleteMenuItem';
import SparkelIcon, { SparkelIconProps } from './icon/SparkelIcon';

export type Item = {
  id: string;
  name: string;
  icon?: React.ReactElement<SparkelIconProps>;
  /** Optional suffix (e.g. badge) rendered after the name */
  suffix?: React.ReactNode;
};

type RenameDeleteListItemProps = {
  item: Item;
  onDelete?: (id: string) => Promise<void>;
  onRename?: (value: string) => Promise<void>;
  showOnHover?: boolean;
  validateUniqueName?: (value: string) => string | undefined;
  nameRegisterOptions?: RegisterOptions;
  inputSize?: ThemingProps['size'];
  padding?: number;
  showDeleteConfirmation?: boolean;
  children?:
    | React.ReactElement<MenuItemProps>
    | React.ReactElement<MenuItemProps>[];
};

// eslint-disable-next-line complexity
export default function RenameDeleteListItem({
  item,
  onDelete,
  onRename,
  inputSize,
  padding,
  showOnHover = false,
  validateUniqueName,
  nameRegisterOptions,
  children: menuItems,
  showDeleteConfirmation = true,
}: RenameDeleteListItemProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, errors, isSubmitting },
  } = useForm<{ name: string }>({ mode: 'onChange' });

  const error = errors?.name;

  const { t } = useTranslation('common');

  const onSubmitInternal = handleSubmit(async (value) => {
    if (onRename) {
      await onRename(value.name);
      setIsEditing(false);
    }
  });

  const [isEditing, setIsEditing] = useState(false);

  const onCancelRenaming = () => {
    setIsEditing(false);
    reset();
  };

  let registerOptions = nameRegisterOptions;
  if (validateUniqueName) {
    registerOptions = {
      ...registerOptions,
      validate: {
        ...nameRegisterOptions?.validate,
        uniqueName: validateUniqueName,
      },
    };
  }

  const ref = useRef(null);
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <ListItem
      key={item.id}
      role={showOnHover ? 'group' : undefined}
      borderRadius="md"
      _hover={{ bg: hoverBg }}
      transition="background 0.2s"
    >
      {!isEditing ? (
        <Flex justifyContent={'flex-start'} ref={ref} p={padding ?? 2}>
          <Flex
            justifyContent="space-between"
            alignContent="center"
            width="100%"
          >
            <HStack spacing={2}>
              <Text>{item.name}</Text>
              {item.suffix}
            </HStack>
            <Box mr={2}>{item.icon}</Box>
          </Flex>

          <Menu>
            <Tooltip label={t('three-dot-menu.tooltip')}>
              <MenuButton
                aria-label="More settings"
                ml="auto"
                as={IconButton}
                size={'xs'}
                maxWidth={16}
                colorScheme="gray"
                opacity={showOnHover ? 0 : undefined}
                _groupHover={
                  showOnHover
                    ? {
                        opacity: 1,
                        transition: 'opacity 0.5s ease-in',
                      }
                    : undefined
                }
              >
                <SparkelIcon icon={faEllipsisH} />
              </MenuButton>
            </Tooltip>
            <Portal containerRef={ref}>
              <MenuList>
                {menuItems}
                {onRename && (
                  <MenuItem
                    aria-label="Rename"
                    icon={<SparkelIcon icon={faPen} />}
                    onClick={() => setIsEditing(true)}
                  >
                    {t('three-dot-menu.rename')}
                  </MenuItem>
                )}
                {onDelete &&
                  (showDeleteConfirmation ? (
                    <DeleteMenuItem
                      name={item.name}
                      onDelete={() => onDelete(item.id)}
                    />
                  ) : (
                    <MenuItem
                      aria-label="Delete"
                      icon={
                        <SparkelIcon
                          icon={faTrash}
                          color="red.400"
                          fixedWidth
                        />
                      }
                      onClick={() => onDelete(item.id)}
                      textColor="red.400"
                    >
                      {t('three-dot-menu.delete')}
                    </MenuItem>
                  ))}
              </MenuList>
            </Portal>
          </Menu>
        </Flex>
      ) : onRename ? (
        <form onSubmit={onSubmitInternal} id="rename-form">
          <HStack ml={-2} alignItems="center">
            <FormControl isRequired>
              <Popover
                placement="top-end"
                isOpen={error && error.type !== 'required'}
                autoFocus={false}
              >
                <PopoverAnchor>
                  <Input
                    size={inputSize}
                    {...register(`name`, {
                      ...registerOptions,
                    })}
                    placeholder="Name"
                    defaultValue={item.name}
                    autoFocus={true}
                  />
                </PopoverAnchor>
                <PopoverContent>
                  <Alert status="error" fontSize="sm">
                    <AlertIcon />
                    <AlertTitle>{error?.message}</AlertTitle>
                  </Alert>
                </PopoverContent>
              </Popover>
            </FormControl>
            <IconButton
              aria-label="Cancel"
              colorScheme="gray"
              size="xs"
              icon={<SparkelIcon icon={faTimes} />}
              onClick={onCancelRenaming}
            />
            <IconButton
              aria-label="Save"
              size="xs"
              isDisabled={!isValid}
              type="submit"
              form="rename-form"
              isLoading={isSubmitting}
              icon={
                <SparkelIcon icon={faCheck} color={['gray.50', 'gray.700']} />
              }
            />
          </HStack>
        </form>
      ) : null}
    </ListItem>
  );
}
